import { useEffect, useState } from 'react';
import { fetchOffer, type Offer } from '../lib/offer';

/**
 * The price and the ways to pay it, asked for once.
 *
 * Deliberately not keyed on the account, which is what separates this from
 * `usePaid`. What a learner *owns* is a fact about them and is re-read every
 * time they sign in or out; what the course *costs* is a fact about the
 * deployment and is the same for a stranger and for the author. Asking again
 * on every account change would be a request per sign-in for an answer that
 * cannot have moved.
 *
 * Null while it is in flight and null if it could not be had, and the offer
 * block draws nothing in either case. That is the honest behaviour for a
 * price: a page that renders a placeholder where the number goes has told the
 * reader there is a number and then not shown it, and a page that renders a
 * buy button with no price beside it is worse than one that waits a beat.
 */
export function useOffer(): Offer | null {
  const [offer, setOffer] = useState<Offer | null>(null);

  useEffect(() => {
    let live = true;
    void fetchOffer().then((next) => {
      if (live) setOffer(next);
    });
    return () => {
      live = false;
    };
  }, []);

  return offer;
}
